import { useState } from "react";
import { DropdownButton, Dropdown } from "react-bootstrap";
import PropertiesCard from "./PropertyCard";
import Pagination from "./shared/Pagination";
import { handleDownload } from "../utils";
import { isHybridSale } from "../../utilities/hybridSales";
import { findFavorite } from "../../utilities/favoritesUtils";

const ITEMS_PER_PAGE = 9;

/**
 * Renders a single sale-date group: its header, download dropdown,
 * paginated property cards, and top/bottom pagination controls.
 *
 * @param {object[]} properties       - all properties in this sale group
 * @param {string}   saleIdDateKey    - unique key for this group (saleId-date)
 * @param {string}   selectedState
 * @param {string}   selectedTimeFrame
 * @param {object[]} favorites        - user's favourites array from Redux
 * @param {object[]} registrations    - user's registrations array from Redux
 * @param {function} onDetailsClick
 * @param {function} onRegisterClick
 * @param {function} onAddFavorite
 * @param {function} onRemoveFavorite
 * @param {function} isPropertyFavorite
 * @param {function} isPropertyRegistered
 * @param {function} triggerToast
 */
const SaleGroup = ({
  properties,
  saleIdDateKey,
  selectedState,
  selectedTimeFrame,
  favorites,
  registrations,
  onDetailsClick,
  onRegisterClick,
  onAddFavorite,
  onMapClick,
  onRemoveFavorite,
  isPropertyFavorite,
  isPropertyRegistered,
  triggerToast,
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  if (!properties || properties.length === 0) return null;

  const first = properties[0];
  const totalPages = Math.ceil(properties.length / ITEMS_PER_PAGE);
  const paginated = properties.slice(currentPage * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE);
  const isCancelled = !!first.cancelled;

  const typeForHeader = isHybridSale(first.location) ? "In-Person" : first.type;

  return (
    <div className="county-details mb-5">
      <h3>
        {isCancelled ? (
          <>
            <span className="sale-group-cancelled-text">
              {`${first.county}, ${first.state} — ${typeForHeader} ${first.saleTypeDescription} — ${first.auctionDate}`}
            </span>
            <span className="sale-group-cancelled-label">Sale Cancelled</span>
          </>
        ) : (
          `${first.county}, ${first.state} — ${typeForHeader} ${first.saleTypeDescription} — ${first.auctionDate}`
        )}
      </h3>

      <div className="download-list-button-container text-center mb-3">
        <DropdownButton
          id={`download-${saleIdDateKey}`}
          className="custom-dropdown-button"
          title="Download List"
          container={document.body}
          disabled={isCancelled}
        >
          {["excel", "pdf"].map((format) => (
            <Dropdown.Item
              key={format}
              onClick={() =>
                handleDownload(
                  first.effectiveSaleId,
                  format,
                  first.county,
                  first.saleTypeDescription,
                  new Date().toISOString().split("T")[0],
                  selectedState,
                  selectedTimeFrame
                )
              }
            >
              {format.charAt(0).toUpperCase() + format.slice(1)}
            </Dropdown.Item>
          ))}
        </DropdownButton>
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      <div className="property-cards-container d-flex flex-wrap justify-content-center">
        {paginated.map((property, idx) => {
          const favorite = findFavorite(favorites, property);
          const favoriteId = favorite?.favorite_id;

          return (
            <PropertiesCard
              key={favoriteId || `${property.propertyId}-${idx}`}
              property={property}
              favoriteId={favoriteId}
              originalSaleId={property.originalSaleId}
              handleDetailsClick={onDetailsClick}
              handleRegisterOrUnregisterClick={({ saleId: effectiveSaleId, auction }) => {
                if (effectiveSaleId === null || effectiveSaleId === undefined || isNaN(Number(effectiveSaleId))) {
                  triggerToast("No valid saleId.");
                  return;
                }
                onRegisterClick({ saleId: effectiveSaleId, auction });
              }}
              onMapClick={onMapClick}
              isPropertyFavorite={isPropertyFavorite}
              isRegistered={isPropertyRegistered(property, registrations)}
              getFavoriteButtonLabel={() => (isPropertyFavorite(property) ? "Unfavorite" : "Favorite")}
              getRegisterButtonLabel={() => (isPropertyRegistered(property, registrations) ? "Unregister" : "Register")}
              removeFavoriteFunc={() => {
                if (!favoriteId) {
                  triggerToast("No favorite_id.");
                  return;
                }
                onRemoveFavorite(favoriteId);
              }}
              addFavoriteFunc={() => onAddFavorite(property)}
              userRegistrations={registrations}
              propIndex={idx}
            />
          );
        })}
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  );
};

export default SaleGroup;
